import type { SyntheticEvent } from "react"
import type { LinkAtmProps } from "@onestore/hel/dist/components/atoms/LinkAtm"
import type { PriceType } from "@core/types"
import type { CtaAXLink } from "@gatsby-plugin-generic-page/fragments/ctaAXLink"
import type { InternalLink } from "@gatsby-plugin-generic-page/fragments/internalLink"
import type { LinkElement } from "@gatsby-plugin-generic-page/fragments/linkElement"
import type { LinkElementWrapper } from "@gatsby-plugin-generic-page/fragments/linkElementWrapper"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { typeMatches } from "@onestore-graphql"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import type {
  AnchorId,
  ExternalLink,
  SquidexMarkdownEmbeddedContent,
} from "~/types/squidex"
import { getAnchorProperties } from "../../../../src/lib/dom"

type LinkPropsType = Omit<LinkAtmProps, "typography"> & {
  textWithEmbeddedContent?: SquidexMarkdownEmbeddedContent
  axLinkData?: CtaAXLink
}

function getLinkProps(
  data: LinkElement | LinkElementWrapper,
  currentPriceType: PriceType
): LinkPropsType {
  let text: string | undefined
  let textWithEmbeddedContent: SquidexMarkdownEmbeddedContent | undefined
  const { title, gaEvent, link } = data

  if (typeMatches(data.__typename, SquidexComponentType.LINK)) {
    const linkElementData = data as LinkElement
    text = linkElementData.text.text
    textWithEmbeddedContent = linkElementData.text
  }

  let href: string | undefined
  let to: string | undefined
  let openInNewTab: boolean | undefined
  let onClick: ((event: SyntheticEvent) => void) | undefined
  let axLinkData: CtaAXLink | undefined

  if (typeMatches(link.__typename, SquidexComponentType.EXTERNAL_LINK)) {
    const { href: hrefLink, openInNewTab: openNewTab } = link as ExternalLink

    href = hrefLink
    openInNewTab = openNewTab
  }

  if (typeMatches(link.__typename, SquidexComponentType.ANCHOR_ID)) {
    const { anchorId } = link as AnchorId
    const anchorProperties = getAnchorProperties(anchorId)

    href = anchorProperties.href
    onClick = anchorProperties.onClick
  }

  if (typeMatches(link.__typename, SquidexComponentType.INTERNAL_LINK)) {
    const { pages, openInNewTab: openNewTab } = link as InternalLink

    if (!isEmpty(pages)) {
      to = !openNewTab ? pages[0].flatData.url : undefined
      href = openNewTab ? pages[0].flatData.url : undefined
      openInNewTab = openNewTab
    }
  }

  if (typeMatches(link.__typename, SquidexComponentType.CTA_AX_LINK)) {
    axLinkData = link as CtaAXLink
    href = url.generateBonusUrlByCtaAxLink(axLinkData, currentPriceType)
  }

  const handleOnClick =
    onClick || gaEvent
      ? (event) => {
          if (onClick) {
            onClick(event)
          }

          sendGaEvent(gaEvent)
        }
      : undefined

  return {
    to,
    href,
    text,
    textWithEmbeddedContent,
    title,
    openInNewTab,
    onClick: handleOnClick,
    axLinkData,
    // `title`'s declared type is a required `string` (via LinkAtmProps),
    // but `data.title` is genuinely optional upstream — pre-existing and
    // out of scope for this fix, so the cast is kept for that field only.
  } as LinkPropsType
}

export default getLinkProps
